import type { ChecklistItemDisplayState, EffectiveChecklistItemStatus } from '../types';

export type OrderedItemStatus = {
  id: string;
  status: EffectiveChecklistItemStatus | undefined;
};

/** A step counts as "done" — for gating, progress, and completion — when complete or skipped. */
export function isDone(status: EffectiveChecklistItemStatus | undefined): boolean {
  return status === 'complete' || status === 'skipped';
}

/**
 * Maps ordered statuses onto display states, gating sequentially: every step up to the first
 * not-complete step is `complete`, that first not-complete step is `active`, and everything after it
 * is `locked` — including a completed step that sits behind an earlier incomplete one (it can't be
 * "done" out of order). Loading (`undefined`) items are omitted but still gate the steps that follow.
 *
 * With `gated` false the sequential lock is lifted: nothing is ever `locked`, so done steps read
 * `complete` regardless of order and every pending step reads `active` (open and interactable). The
 * first pending step is still the first `active` one, so "next step" selection is unchanged.
 */
export function deriveDisplayState(
  items: OrderedItemStatus[],
  gated = true,
): Map<string, ChecklistItemDisplayState> {
  const displayStates = new Map<string, ChecklistItemDisplayState>();
  // True once a preceding step is not complete; later completes then read as `locked`, not `complete`.
  let blocked = false;
  let activeAssigned = false;

  for (const item of items) {
    if (item.status === undefined) {
      blocked = true;
      continue;
    }
    if (isDone(item.status)) {
      // Skipped reads the same as complete here: done, re-expandable, doesn't gate — only the
      // item's badge distinguishes the two. Done-out-of-order still locks behind an earlier gap.
      displayStates.set(item.id, gated && blocked ? 'locked' : 'complete');
      continue;
    }
    // Ungated: every pending step is `active` — none lock. `.find`-based "next step" selection still
    // resolves to the first one, so callers that pick the active step are unaffected.
    if (!gated) {
      displayStates.set(item.id, 'active');
      continue;
    }
    // Pending: the first one is `active`; any pending after it is `locked`. Either way it gates.
    if (!activeAssigned) {
      displayStates.set(item.id, 'active');
      activeAssigned = true;
    } else {
      displayStates.set(item.id, 'locked');
    }
    blocked = true;
  }

  return displayStates;
}
